/* When one binding runs, as the design asks it: one question, three answers.
 *
 * **One choice rather than two cards.** A binding can technically carry a schedule *and* a harvest
 * trigger, and the old dialog showed both side by side with every fact each one stores — the next
 * run, the last run, the outcome, the cooldown, the harvest that caused it. That is a status page,
 * and it buried the decision somebody opened the dialog to make. The design asks "when should this
 * binding run?" and offers on a schedule, after a harvest, or manually; the status lives on the
 * Executions screen, where the runs are.
 *
 * **Choosing one switches the other off — it does not delete it.** Saving "on a schedule" disables
 * an enabled harvest trigger and keeps its configuration, so choosing "after a harvest" again later
 * finds the connection already filled in. Manual disables both. When both are on today the dialog
 * says so before anything is saved, because a save will narrow it to one.
 *
 * **No default strategy.** Full and sampled differ by orders of magnitude in what they read from a
 * customer's database, so neither card starts selected for a binding that has never been scheduled:
 * a pre-selected Full would make the expensive promise silently.
 */

import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ExecutionSchedule, ExecutionTrigger } from '@/shared/api/generated/rules';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';

import {
  useConfigureExecutionSchedule,
  useConfigureExecutionTrigger,
  useExecutionSchedule,
  useExecutionTrigger,
  useSetExecutionScheduleEnabled,
  useSetExecutionTriggerEnabled,
} from '../api/scheduling';
import { nextOccurrence } from '../model/occurrence';
import {
  SCHEDULABLE_STRATEGIES,
  SEVERITY_FLOORS,
  TIMEZONES,
  WEEKDAY_ORDER,
  formatInstant,
  presenceOf,
  recurrenceOf,
  recurrenceProblem,
  weekdayKey,
  withCadence,
  type RecurrenceDraft,
} from '../model/presentation';

type Mode = 'schedule' | 'harvest' | 'manual';
type Strategy = 'full' | 'sampled';
type Floor = 'blocker' | 'error' | 'warning' | 'info';

const MODES: readonly Mode[] = ['schedule', 'harvest', 'manual'];

export interface BindingSchedulingProps {
  readonly bindingId: string;
  readonly connections: readonly { readonly id: string; readonly name: string }[];
  /** Called after a save lands, and by Cancel. */
  readonly onDone: () => void;
}

export function BindingScheduling({
  bindingId,
  connections,
  onDone,
}: BindingSchedulingProps): ReactElement {
  const { t } = useTranslation();
  const schedule = useExecutionSchedule(bindingId);
  const trigger = useExecutionTrigger(bindingId);

  if (schedule.isError || trigger.isError) {
    const error = schedule.error ?? trigger.error;
    const forbidden = error instanceof ApiError && error.status === 403;
    return (
      <div className="lq-when">
        <div className="lq-when__body">
          <p className="lq-ac__error" role="alert">
            {forbidden
              ? t('scheduling.forbidden')
              : t(
                  schedule.isError
                    ? 'scheduling.execution.readFailed'
                    : 'scheduling.trigger.readFailed',
                )}
          </p>
        </div>
        <div className="lq-when__foot">
          <Button onClick={onDone}>{t('scheduling.form.cancel')}</Button>
        </div>
      </div>
    );
  }

  if (!schedule.isSuccess || !trigger.isSuccess) {
    return (
      <div className="lq-when">
        <div className="lq-when__body">
          <p className="lq-when__quiet">{t('scheduling.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <WhenForm
      bindingId={bindingId}
      schedule={schedule.data}
      trigger={trigger.data}
      connections={connections}
      onDone={onDone}
    />
  );
}

function initialMode(schedule: ExecutionSchedule, trigger: ExecutionTrigger): Mode {
  if (schedule.configured && schedule.enabled) return 'schedule';
  if (trigger.configured && trigger.enabled) return 'harvest';
  return 'manual';
}

function WhenForm({
  bindingId,
  schedule,
  trigger,
  connections,
  onDone,
}: {
  readonly bindingId: string;
  readonly schedule: ExecutionSchedule;
  readonly trigger: ExecutionTrigger;
  readonly connections: readonly { readonly id: string; readonly name: string }[];
  readonly onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const configureSchedule = useConfigureExecutionSchedule();
  const configureTrigger = useConfigureExecutionTrigger();
  const setScheduleEnabled = useSetExecutionScheduleEnabled();
  const setTriggerEnabled = useSetExecutionTriggerEnabled();

  const [mode, setMode] = useState<Mode>(() => initialMode(schedule, trigger));
  const [draft, setDraft] = useState<RecurrenceDraft>(() => recurrenceOf(schedule));
  const [strategy, setStrategy] = useState<Strategy | ''>(
    () => schedule.strategy ?? trigger.strategy ?? '',
  );
  const [floor, setFloor] = useState<Floor>(
    () => schedule.severity_floor ?? trigger.severity_floor ?? 'info',
  );
  const [connectionId, setConnectionId] = useState(() => trigger.connection_id ?? '');
  const [shown, setShown] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const repeatsId = useId();
  const daysId = useId();
  const strategyId = useId();

  const scheduleOn = schedule.configured && schedule.enabled;
  const triggerOn = trigger.configured && trigger.enabled;
  const presence = presenceOf(schedule);
  const preview = mode === 'schedule' ? nextOccurrence(draft, new Date()) : null;

  function problemOf(): string | null {
    if (mode === 'schedule') {
      const recurrence = recurrenceProblem(draft);
      if (recurrence !== null) return t(recurrence);
      if (strategy === '') return t('scheduling.when.error.strategy');
    }
    if (mode === 'harvest') {
      if (connectionId === '') return t('scheduling.form.invalid.noConnection');
      if (strategy === '') return t('scheduling.when.error.strategy');
    }
    return null;
  }

  async function save(): Promise<void> {
    const problem = problemOf();
    setShown(problem);
    if (problem !== null) return;
    setSaving(true);
    setFailure(null);
    try {
      if (mode === 'schedule' && strategy !== '') {
        await configureSchedule.mutateAsync({
          bindingId,
          body: {
            enabled: true,
            cadence: draft.cadence,
            local_time: draft.localTime.trim(),
            timezone: draft.timezone,
            weekdays: [...draft.weekdays],
            strategy,
            severity_floor: floor,
          },
        });
        if (triggerOn)
          await setTriggerEnabled.mutateAsync({ id: bindingId, enabled: false });
      } else if (mode === 'harvest' && strategy !== '') {
        await configureTrigger.mutateAsync({
          bindingId,
          body: {
            enabled: true,
            connection_id: connectionId,
            strategy,
            severity_floor: floor,
          },
        });
        if (scheduleOn) {
          await setScheduleEnabled.mutateAsync({ id: bindingId, enabled: false });
        }
      } else {
        if (scheduleOn) {
          await setScheduleEnabled.mutateAsync({ id: bindingId, enabled: false });
        }
        if (triggerOn)
          await setTriggerEnabled.mutateAsync({ id: bindingId, enabled: false });
      }
      onDone();
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.status === 403
            ? t('scheduling.forbidden')
            : error.message
          : t('scheduling.form.saveFailed'),
      );
    } finally {
      setSaving(false);
    }
  }

  const toggleDay = (day: number): void => {
    setDraft({
      ...draft,
      weekdays: draft.weekdays.includes(day)
        ? draft.weekdays.filter((each) => each !== day)
        : [...draft.weekdays, day].sort((a, b) => a - b),
    });
  };

  const strategyCards = (
    <div className="lq-ac__field">
      <span id={strategyId} className="lq-visually-hidden">
        {t('scheduling.form.strategy')}
      </span>
      <div className="lq-when__cards" role="group" aria-labelledby={strategyId}>
        {SCHEDULABLE_STRATEGIES.map((each) => (
          <button
            key={each}
            type="button"
            className="lq-when__card"
            aria-pressed={strategy === each}
            onClick={() => {
              setStrategy(each);
            }}
          >
            <span className="lq-when__cardTitle">{t(`scheduling.strategy.${each}`)}</span>
            <span className="lq-when__cardSub">
              {t(`scheduling.when.strategy.${each}`)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  const floorSelect = (
    <label className="lq-ac__field">
      {t('scheduling.when.floor')}
      <select
        className="lq-input"
        value={floor}
        onChange={(event) => {
          setFloor(event.target.value as Floor);
        }}
      >
        {[...SEVERITY_FLOORS].reverse().map((each) => (
          <option key={each} value={each}>
            {t(`scheduling.when.floor.${each}`)}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="lq-when">
      <div className="lq-when__body">
        <SegmentedFilter
          variant="track"
          label={t('scheduling.when.modes')}
          value={mode}
          onChange={(next) => {
            setMode(next);
            setShown(null);
          }}
          options={MODES.map((each) => ({
            id: each,
            label: t(`scheduling.when.mode.${each}`),
          }))}
        />

        {scheduleOn && triggerOn ? (
          <p className="lq-when__note">{t('scheduling.when.both')}</p>
        ) : null}
        {mode === 'schedule' && presence.kind === 'suspended' ? (
          <p className="lq-ac__warn" title={presence.reason}>
            {t('scheduling.when.suspended', {
              when: formatInstant(presence.until) ?? presence.until,
            })}
          </p>
        ) : null}

        {mode === 'schedule' ? (
          <div className="lq-ac__form">
            <div className="lq-ac__fields">
              <div className="lq-ac__field">
                <span id={repeatsId}>{t('scheduling.harvest.repeats')}</span>
                <div className="lq-ac__seg" role="group" aria-labelledby={repeatsId}>
                  {(['daily', 'weekly'] as const).map((cadence) => (
                    <button
                      key={cadence}
                      type="button"
                      className="lq-ac__segOption"
                      aria-pressed={draft.cadence === cadence}
                      onClick={() => {
                        setDraft(withCadence(draft, cadence));
                      }}
                    >
                      {t(`scheduling.cadence.${cadence}`)}
                    </button>
                  ))}
                </div>
              </div>
              <label className="lq-ac__field">
                {t('scheduling.harvest.timeLabel')}
                <input
                  className="lq-input lq-mono lq-ac__time"
                  value={draft.localTime}
                  inputMode="numeric"
                  placeholder="06:00"
                  onChange={(event) => {
                    setDraft({ ...draft, localTime: event.target.value });
                  }}
                />
              </label>
              <label className="lq-ac__field lq-when__zone">
                {t('scheduling.form.timezone')}
                <select
                  className="lq-input"
                  value={draft.timezone}
                  title={t('scheduling.form.timezoneHint')}
                  onChange={(event) => {
                    setDraft({ ...draft, timezone: event.target.value });
                  }}
                >
                  {TIMEZONES.map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {draft.cadence === 'weekly' ? (
              <div className="lq-ac__days" role="group" aria-labelledby={daysId}>
                <span id={daysId} className="lq-visually-hidden">
                  {t('scheduling.form.weekdays')}
                </span>
                {WEEKDAY_ORDER.map((day) => (
                  <button
                    key={day}
                    type="button"
                    className="lq-ac__day"
                    aria-pressed={draft.weekdays.includes(day)}
                    onClick={() => {
                      toggleDay(day);
                    }}
                  >
                    {t(weekdayKey(day))}
                  </button>
                ))}
              </div>
            ) : null}

            {strategyCards}
            {floorSelect}

            {preview !== null ? (
              <p className="lq-ac__preview">
                <span className="lq-ac__previewDot" aria-hidden="true" />
                {t('scheduling.when.preview', { when: preview, zone: draft.timezone })}
              </p>
            ) : null}
          </div>
        ) : null}

        {mode === 'harvest' ? (
          <div className="lq-ac__form">
            <label className="lq-ac__field">
              {t('scheduling.when.harvestOf')}
              <select
                className="lq-input"
                value={connectionId}
                onChange={(event) => {
                  setConnectionId(event.target.value);
                }}
              >
                <option value="">{t('scheduling.when.chooseConnection')}</option>
                {connections.map((each) => (
                  <option key={each.id} value={each.id}>
                    {each.name}
                  </option>
                ))}
              </select>
            </label>
            <p className="lq-when__quiet">{t('scheduling.when.harvestNote')}</p>
            {strategyCards}
            {floorSelect}
          </div>
        ) : null}

        {mode === 'manual' ? (
          <p className="lq-when__manual">{t('scheduling.when.manual')}</p>
        ) : null}

        {shown !== null ? (
          <p className="lq-ac__error" role="alert">
            {shown}
          </p>
        ) : null}
        {failure !== null ? (
          <p className="lq-ac__error" role="alert">
            {failure}
          </p>
        ) : null}
      </div>

      <div className="lq-when__foot">
        <Button onClick={onDone}>{t('scheduling.form.cancel')}</Button>
        <Button
          tone="primary"
          busy={saving}
          onClick={() => {
            void save();
          }}
        >
          {t('scheduling.when.save')}
        </Button>
      </div>
    </div>
  );
}
